import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Activity, ClipboardCheck, Timer, Camera,
  RefreshCw, ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * R141 — Mobile Tech Pace Board.
 *
 * "How am I doing this month?" — designed for the phone-only Tech shell.
 * Shows three headline metrics + a month picker for glancing back at
 * prior periods. Deliberately spartan — techs open this between calls
 * so read-time must be < 2 seconds.
 */
function fmtDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function currentMonthYYYYMM(date = new Date()) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function shiftMonth(yyyyMm, delta) {
  const [y, m] = yyyyMm.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return currentMonthYYYYMM(d);
}

const MetricTile = ({ label, value, sub, icon: Icon, accent }) => (
  <Card className="p-4 bg-white/[0.03] border-white/10">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <div className={`text-3xl font-semibold tabular-nums ${accent}`}>{value}</div>
    {sub && <div className="text-[11px] text-white/50 mt-1">{sub}</div>}
  </Card>
);

export default function TechPacePage() {
  const nav = useNavigate();
  const [month, setMonth] = useState(currentMonthYYYYMM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nowYm = currentMonthYYYYMM();
  const atCurrent = month === nowYm;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tech/pace", { params: { month } });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load pace");
      setData(null);
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="tech-pace-page">
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" variant="ghost" onClick={() => nav("/tech")}
                className="text-white/70 hover:text-white -ml-2"
                data-testid="tech-pace-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-orange-400">Your pace</div>
          <h1 className="text-2xl font-semibold font-display">This month</h1>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="tech-pace-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Month picker */}
      <div className="flex items-center justify-between mb-4">
        <Button size="sm" variant="outline"
                onClick={() => setMonth(shiftMonth(month, -1))}
                className="border-white/10 h-8 px-2"
                data-testid="tech-pace-prev">
          <ChevronRight className="h-3.5 w-3.5 rotate-180" />
        </Button>
        <div className="text-sm tabular-nums font-medium" data-testid="tech-pace-label">
          {month}
        </div>
        <Button size="sm" variant="outline" disabled={atCurrent}
                onClick={() => setMonth(shiftMonth(month, 1))}
                className="border-white/10 h-8 px-2 disabled:opacity-40"
                data-testid="tech-pace-next">
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 gap-3">
        <MetricTile
          label="Tickets closed"
          value={data?.tickets_closed ?? 0}
          sub={atCurrent ? "So far this month" : "Total for the month"}
          icon={ClipboardCheck}
          accent="text-emerald-300"
        />
        <MetricTile
          label="Avg time to accept"
          value={fmtDuration(data?.avg_accept_seconds)}
          sub={data?.sample_size_for_accept
            ? `Across ${data.sample_size_for_accept} accepted tickets`
            : "No accepted tickets yet"}
          icon={Timer}
          accent="text-sky-300"
        />
        <MetricTile
          label="Photo attach rate"
          value={`${data?.photo_attach_rate ?? 0}%`}
          sub={`${data?.photos_attached_count ?? 0} of ${data?.tickets_closed ?? 0} closed tickets have a photo`}
          icon={Camera}
          accent="text-amber-300"
        />
      </div>

      <Card className="p-4 mt-4 bg-orange-500/[0.06] border-orange-500/20 text-xs text-orange-100/90 flex gap-3"
            data-testid="tech-pace-tip">
        <Activity className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <b>Pro tip:</b> a photo attached to every completed ticket
          protects you if a resident disputes the work later. Aim for
          80%+ attach rate on visible fixes.
        </div>
      </Card>
    </div>
  );
}
